// Wire-shape types matching the mock's responses (snake_case, per spec §7).
import type { CliAuthTokenPolicy } from '@mg/shared/cli-auth-policy';
import type { OrgGroupDefinition } from '@mg/shared/org';
import type { UserClass } from '@mg/shared/types';
import type { UserClassReason, UserClassSource } from '@mg/shared/user-class';
import type { OrgIdentityDiagnostic } from '@mg/shared/org-service';
export type {
  GuestGroupsResponse, GuestEvidenceStatus, SetUserClassInput, UserClassReason,
  UserClassSource,
  UserClassRepairPreview, UserClassRepairTarget, UserClassRepairApplyInput, UserClassRepairApplyResponse,
  UserClassResetInput, UserClassResetResponse,
  GuestGroupImpact,
} from '@mg/shared/user-class';

export type { CliInstallGrantResponse } from '@mg/shared/cli-install';
export type {
  OrgPerson,
  OrgFacet,
  OrgCatalog,
  OrgTreeNode,
  OrgTree,
  OrgUser,
  OrgSearch,
  OrgGroupDefinition,
  OrgGroup,
  OrgGroups,
  OrgQuery,
  OrgQueryResult,
  OrgRpcResult,
} from '@mg/shared/org';
export type { OrgRuntime as ApiOrgRuntime } from '@mg/shared/org-service';

/** Nonsecret authenticated device / OMP approval metadata; not OAuth scopes. */
export interface ApiCliAuthRequest {
  req_id: string;
  client_name: string | null;
  provenance: ApiTokenProvenance | null;
  token_policy: CliAuthTokenPolicy;
  approved: boolean;
  created_at: number;
  expires_at: number;
  expired: boolean;
}

export interface ApiCliDeviceAuthRequest extends ApiCliAuthRequest {
  approved_at: number | null;
}

/** MCP OAuth consent request details returned by GET /api/v1/mcp/consent/<req_id>. */
export interface ApiMcpConsentDetails {
  req_id: string;
  client_name: string;
  redirect_uri: string;
  scopes: string[];
  resource: string;
  expires_at: number;
  /**
   * Present when the resource URL is a per-app MCP gateway (path or subdomain
   * form). Null/omitted for platform-wide consent.
   */
  app_context?: ApiMcpConsentAppContext | null;
}

export interface ApiMcpConsentAppContext {
  namespace: {
    stub: string;
    tier: 'user' | 'team' | 'brand';
    display_name: string | null;
    owner: {
      user_id: string;
      display_name: string | null;
      email: string | null;
    } | null;
  };
  app: {
    id: string;
    name: string;
    kind: 'static' | 'worker' | 'mcp' | 'llm';
  };
  tools: Array<{
    name: string;
    kind: 'read' | 'write' | 'other';
    description: string;
    enabled: boolean;
    privileged: boolean;
  }>;
}

export interface ApiNamespace {
  id: string;
  tier: 'user' | 'team' | 'brand';
  stub: string;
  display_name?: string;
  description?: string;
  owner_user_id?: string;
  state: 'active' | 'archived';
  created_at: number;
  created_by: string;
  home_app_id?: string;
  guest_group?: boolean;
  viewer?: { can_add_members: boolean };
}

export type Sharing = 'restricted' | 'internally-public' | 'unlisted';

/**
 * Per-viewer capability flags returned on every app payload. The console
 * reads these directly to decide what to render: hide nav links to logs/
 * env/audit when has_internal_access=false, hide the deploy file viewer
 * when can_view_app_code=false, hide write buttons when can_manage /
 * can_deploy are false.
 */
export interface ApiAppViewer {
  has_internal_access: boolean;
  can_manage: boolean;
  can_deploy: boolean;
  /**
   * True only for the app owner and platform admins. Strictly stricter than
   * has_internal_access: namespace co-members can read logs/env/audit but
   * NOT the per-deploy file viewer (source contents). Used to hide the
   * "Files" link on the deploys list and to redirect the file-viewer page.
   */
  can_view_app_code: boolean;
  /**
   * True when the viewer reached this app via an actual `app_grants` row —
   * direct user grant or via group membership. False for owners, namespace
   * members, brand-public viewers, and platform admins using the admin
   * override. The home page filters the "Shared" tab on this so admins don't
   * see every app on the platform there.
   */
  granted_to_me: boolean;
  /**
   * True when the viewer can publish this app to external 3rd parties via a
   * signed link (Share Door). Folds in canManageApp + the grantable
   * `external-share` platform capability + static-kind. Gates the
   * "Share externally" panel.
   */
  can_share_externally: boolean;
}

/** An external share (Share Door) — the console-facing projection; never the link. */
export interface ApiExternalShare {
  id: string;
  app_id: string;
  share_id: string;
  enabled: boolean;
  rev: number;
  content_version: string | null;
  display_name: string | null;
  created_by: string;
  created_at: number;
  not_after: number | null;
  revoked_at: number | null;
  /** The app deploy snapshotted into content_version; compare to app.current_deploy_id for staleness. Null pre-0054. */
  source_deploy_id: string | null;
  /** Free-text operator note (console-only); null when unset. */
  note: string | null;
  /** When set (ms epoch), the share is archived — unpublished (stopped serving) but its row + access logs are kept. Null = active. */
  archived_at: number | null;
}

/** One minted link for a share — metadata only, never the token. */
export interface ApiExternalShareLink {
  id: string;
  /** Short human-visible label (mint nonce) to identify this link in the list. */
  prefix: string;
  rev: number;
  created_by: string;
  created_at: number;
  /** Free-text operator note (console-only); null when unset. */
  note: string | null;
  /** Friendly console-only label shown instead of the prefix; null when unset. */
  alias: string | null;
  /** When set (ms epoch), the link is archived (hidden from the active list). Null = active. */
  archived_at: number | null;
  /** Link expiry deadline (ms epoch), mirroring the JWT exp, for "time left". Null pre-0057. */
  expires_at: number | null;
}

/** One external-share access-log line (server-projected; see @mg/shared ShareLogPayload). */
export interface ApiShareAccessLogRow {
  ts: number;
  request_id: string;
  share_id: string;
  host: string;
  method: string;
  path: string;
  status: number;
  bytes?: number;
  duration_ms: number;
  cf_colo?: string;
  country?: string;
  source_ip?: string;
  ua?: string;
  ref?: string;
  auth_via: 'cookie' | 'link' | 'none';
  cache?: 'hit' | 'miss';
  /** The minted-link id this request came through (link/cookie paths); server-enriched. */
  lid?: string;
  /** Session id (ses_<ulid>) minted at login; groups a visit's requests. */
  sid?: string;
  /** Server-resolved aliases for display, so the row shows names not hex. */
  share_alias?: string | null;
  link_alias?: string | null;
  /** Server-resolved creators (user ids) of the share/link, for a "who made it" link. */
  share_created_by?: string | null;
  link_created_by?: string | null;
  result: 'served' | 'expired' | 'disabled' | 'revoked' | 'not_found' | 'forbidden';
}

/** Access-log stats for an external share over a window. */
export interface ApiExternalShareStats {
  total: number;
  /** True when the server hit its log-scan limit: `total` (and per-share counts) are a floor, not exact. */
  truncated: boolean;
  recent: ApiShareAccessLogRow[];
  from: number;
  to: number;
}

/** Aggregate access log across all of an app's external shares (each row tagged with share_id). */
export interface ApiAppExternalShareLogs {
  total: number;
  /** True when the server hit its log-scan limit: `total` (and per-share counts) are a floor, not exact. */
  truncated: boolean;
  recent: ApiShareAccessLogRow[];
  from: number;
  to: number;
  /** visit count per public share_id in the window (for the summary tiles). */
  per_share_counts: Record<string, number>;
}

export interface ApiApp {
  id: string;
  namespace_id: string;
  name: string;
  hostname: string;
  owner_user_id: string;
  /**
   * True when the app is owned by its (team/brand) namespace rather than an
   * individual — owner_user_id is the synthetic system principal. Drives the
   * "Namespace-owned" badge; access is governed by namespace roles.
   */
  namespace_owned: boolean;
  description?: string;
  kind: 'static' | 'worker' | 'mcp';
  /**
   * Kind-specific runtime config. Omitted from the wire payload for grantees
   * (viewer.has_internal_access=false) — those callers shouldn't see internal
   * routing knobs.
   */
  config?: Record<string, unknown>;
  sharing: Sharing;
  state: 'active' | 'archived';
  current_deploy_id?: string;
  archived_at?: number;
  last_deployed_at?: number;
  created_at: number;
  /** Which mg-resources DO class FD routes /__mg/platform/* to. Omitted for grantees. */
  resource_version?: 'v1' | 'v2';
  /** 'app' = single DO shared across users; 'user' = one DO per user. Omitted for grantees. */
  resource_scope?: 'app' | 'user';
  /**
   * 'open' (default) — anyone with access can write platform storage.
   * 'owner-only' — only the owner + grants with role='writer' can write.
   * Ignored when resource_scope='user' (each user owns their own DO).
   * Omitted for grantees.
   */
  write_protection?: 'open' | 'owner-only';
  /**
   * Migration 0029 — when true, /__mg/platform/sandbox is routed to a
   * SandboxV1 DO; otherwise it returns 404. Worker apps only. Omitted for
   * grantees.
   */
  sandbox_enabled?: boolean;
  /**
   * Migration 0031 — when true, /__mg/platform/container is routed to a
   * ContainerV1 DO (Cloudflare Containers backed); otherwise it returns 404.
   * Static + worker apps. Omitted for grantees.
   */
  container_enabled?: boolean;
  /** Stored org directory access; disabled by default, writable only by platform admins. */
  org_directory_enabled?: boolean;
  /**
   * Migration 0040 — when true, Front Door accepts webhook URL tokens
   * (mg_wt_ in ?__mg_token) under /__webhook/ for this worker app; otherwise
   * such requests 401. Worker apps only. Omitted for grantees.
   */
  url_token_enabled?: boolean;
  /**
   * Migration 0056 — count of PUBLISHED external (public) shares for this app
   * (enabled, not archived). Revoke bumps `rev` to kill outstanding links but
   * leaves the share published, so revoked shares still count. Drives the
   * "Public" badge on the app list + the in-app public-exposure banner.
   * 0/undefined when none.
   */
  external_share_count?: number;
  /**
   * Migration 0029 — MCP gateway transport. 'http' binds the public
   * per-namespace MCP endpoint; 'tunnel' refuses public HTTP and waits for
   * a Managed-Agents handshake. MCP apps only. Omitted for grantees.
   */
  mcp_transport?: 'http' | 'tunnel';
  /**
   * Migration 0061 — 'open' (default) | 'restricted'. When restricted, direct
   * HTTP /__mg/platform/{sql,kv} returns 403 and the store is reachable only
   * through the app's own server code. Worker/mcp apps only. Omitted for
   * grantees.
   */
  http_data_access?: 'open' | 'restricted';
  /**
   * Aggregate of app_grants: total user/group counts + up to 5 preview
   * principals for the home page sharing icon/tooltip. Present for viewers
   * with internal access; omitted for grantees.
   */
  grant_summary?: ApiAppGrantSummary;
  /** Per-viewer capability flags. Always present. */
  viewer: ApiAppViewer;
}

export interface ApiAppGrantSummary {
  users: number;
  groups: number;
  /** True when the built-in "Everyone" virtual group (external_id='everyone') is among the grantees. */
  has_everyone_group?: boolean;
  preview: ReadonlyArray<{ display_name: string; kind: 'user' | 'group' }>;
}

export interface ApiDeploy {
  id: string;
  app_id: string;
  kind: 'static' | 'worker' | 'mcp';
  manifest: Record<string, unknown>;
  bundle_size_bytes: number;
  bundle_sha256: string;
  deployed_by: string;
  deployed_at: number;
  state: 'active' | 'superseded' | 'rolled_back';
  notes?: string;
  has_restore_point: boolean;
  restore_point?: {
    snapshot_set_id: string;
    created_at: number;
    total_row_count: number;
    total_byte_size: number;
  };
}

export interface ApiGrant {
  principal_id: string;
  kind: 'user' | 'group';
  display_name: string;
  granted_by: string;
  granted_at: number;
  /** 'reader' (default) or 'writer'. Only enforced when write_protection='owner-only'. */
  role: 'reader' | 'writer';
  /** Stable principal external_id. Populated only for kind='group'. */
  external_id?: string;
}

export interface ApiMember {
  user_id: string;
  role: 'viewer' | 'member' | 'deploy' | 'namespace_admin';
  user_class: UserClass;
  guest_enrollment_protection: GuestEnrollmentProtection;
  granted_by: string;
  granted_at: number;
  email?: string;
  display_name?: string;
}

export interface ApiNamespaceGroupViewer {
  group_principal_id: string;
  external_id: string;
  display_name: string;
  granted_by: string;
  granted_at: number;
}

export interface ApiIdentity {
  sub: string;
  email: string;
  name: string;
  userNamespaceStub: string | null;
  user_class: UserClass;
  user_class_reason: UserClassReason;
  user_class_source: UserClassSource | null;
  state: 'active';
  platform_admin: boolean;
  memberships: { namespace_id: string; stub: string; tier: string; roles: string[] }[];
  groups: string[];
  principal_id: string;
  /**
   * Present only when the caller is acting as a preview user (an admin has
   * started a preview session). `admin_sub` is the originating admin's id —
   * the console uses it to render the "exit preview" banner and to display
   * who the admin "really" is.
   */
  preview_origin?: { admin_sub: string };
}

export interface ApiPreviewUserGroupRef {
  id: string;
  external_id: string;
  display_name: string;
}

export interface ApiPreviewUserMembership {
  namespace_id: string;
  stub?: string;
  tier?: string;
  role: string;
}

export interface ApiPreviewUser {
  id: string;
  email: string;
  display_name: string;
  user_class: UserClass;
  guest_enrollment_protection: GuestEnrollmentProtection;
  state: 'active' | 'tombstoned';
  user_namespace_stub?: string | null;
  created_at: number;
  groups: ApiPreviewUserGroupRef[];
  memberships: ApiPreviewUserMembership[];
}

export interface ApiEnvVar {
  key: string;
  value: string;
  visibility: 'public' | 'private';
  updated_by: string;
  updated_at: number;
}

export interface ApiAuditEntry {
  id: string;
  action_id: string;
  actor_user_id: string;
  /** Display name from users table; absent if the user row is gone. */
  actor_display_name?: string;
  /** Email from users table; absent if the user row is gone. */
  actor_email?: string;
  action: string;
  target_kind: string;
  target_id: string;
  before_json?: string;
  after_json?: string;
  request_id?: string;
  /** Raw User-Agent header for the request. 'mcp' marks cp-internal RPC calls. */
  user_agent?: string;
  /** api_tokens.id when the mutation authenticated via PAT; absent for cookie auth. */
  auth_token_id?: string;
  /** api_tokens.name — present when auth_token_id resolved against api_tokens. */
  auth_token_name?: string;
  /** api_tokens.kind — 'control_plane' | 'app' | 'app_global' | 'webhook' | 'deploy'. */
  auth_token_kind?: string;
  /** Client source IP recorded at mutation time; absent on older rows. */
  ip?: string;
  /** Mcp-Session-Id when the mutation came through the platform MCP server; absent otherwise. */
  mcp_session_id?: string;
  created_at: number;
}

export type ApiGroupSource = 'directory' | 'manual' | 'virtual' | 'team' | 'org';
export type ApiGroupKind = 'regular' | 'brand' | 'department' | 'manager';

export interface ApiGroupViewer {
  /** Caller may manage the group; Guest-group enrollment and elevation have a separate gate. */
  can_manage: boolean;
  can_add_members: boolean;
  /** Caller's own membership role is 'admin'. */
  is_admin: boolean;
  /** Caller has any membership row in this group. */
  is_member: boolean;
}

export interface ApiGroup {
  id: string;
  external_id: string;
  display_name: string;
  description?: string;
  created_at: number;
  member_count: number;
  source: ApiGroupSource;
  group_kind: ApiGroupKind;
  /** Admin catalog only: current server-validated Guest classification source. */
  guest_classification_eligible?: boolean;
  /**
   * True when this group is configured as a Guest classification source
   * (`guest_groups` row). Explicit platform-admin enrollment can classify non-admin Members as Guests.
   */
  guest_group: boolean;
  /** Per-caller management capability, computed server-side (cf. ApiApp.viewer). */
  viewer: ApiGroupViewer;
  /** Present for team groups (source='team') — the namespace this group mirrors. */
  namespace?: { id: string; stub: string; display_name: string | null };
  /** Present only on authoritative org cohorts. */
  definition?: OrgGroupDefinition;
  last_synced_at?: number | null;
}

export type GuestEnrollmentProtection = 'platform_admin' | 'group_admin' | 'namespace_role' | null;

export interface ApiGroupMember {
  guest_enrollment_protection: GuestEnrollmentProtection;
  user_id: string;
  role: 'member' | 'admin';
  /** The member's current account class. */
  user_class: UserClass;
  email?: string;
  display_name?: string;
}

export interface ApiGroupReferences {
  apps: Array<{ id: string; name: string; hostname: string; namespace_stub: string }>;
  namespaces: Array<{ id: string; stub: string; display_name: string | null }>;
}

export type ApiCohortAccessReason =
  | { kind: 'app-grant'; role: 'reader' | 'writer' }
  | { kind: 'namespace-group'; namespace_id: string }
  | { kind: 'team-membership'; namespace_id: string }
  | { kind: 'org-wide' }
  | { kind: 'unlisted' };

export interface ApiCohortAccessApp {
  id: string;
  name: string;
  hostname: string;
  kind: ApiApp['kind'];
  namespace: {
    id: string;
    stub: string;
    display_name: string | null;
    tier: ApiNamespace['tier'];
    state: 'active' | 'archived' | 'tombstoned';
  };
  sharing: Sharing;
  state: ApiApp['state'];
  current_deploy_id: string | null;
  /** Explicit grant privilege only, not effective platform-data write permission. */
  grant_role: 'reader' | 'writer' | null;
  /**
   * Effective ceiling for guest members of this cohort, independent of the
   * configured reasons above: 'reader' only via explicit grants, else 'none'.
   */
  effective_guest_access: 'reader' | 'none';
  reasons: ApiCohortAccessReason[];
}

export interface ApiCohortAppAccess {
  audience: 'group' | 'organization';
  group: Pick<ApiGroup, 'id' | 'display_name' | 'source' | 'group_kind' | 'member_count'> | null;
  apps: ApiCohortAccessApp[];
}

export interface ApiLlmAlias {
  alias_id: string;
  model: string;
  label: string;
  /**
   * Provider slug, derived server-side from the pricing catalog on every
   * create/update. Load-bearing: front-door's /v1/messages rejects any model
   * whose provider isn't 'anthropic', and Bifrost catalog ids are bare, so
   * there is no prefix to infer it from. Null when the target has no pricing
   * row.
   */
  maker: string | null;
  sort_order: number;
  updated_at: number;
}

export type ApiVirtualRule = { kind: 'everyone' } | { kind: 'email_domain'; domains: string[] };

export interface ApiVirtualGroup {
  id: string;
  external_id: string;
  display_name: string;
  created_at: number;
  source: ApiGroupSource;
  group_kind: ApiGroupKind;
  rule: ApiVirtualRule;
  last_recomputed_at: number | null;
  member_count: number;
}

export interface ApiUserGroupPreview {
  external_id: string;
  display_name: string;
}

export interface ApiUserSummary {
  id: string;
  email: string;
  display_name: string;
  user_class: UserClass;
  user_class_reason: UserClassReason;
  user_class_source: UserClassSource | null;
  state: 'active' | 'tombstoned';
  user_namespace_id?: string;
  user_namespace_stub?: string | null;
  first_login_at?: number;
  last_login_at?: number;
  active_apps_count?: number;
  archived_apps_count?: number;
  groups_total?: number;
  groups_preview?: ApiUserGroupPreview[];
  /** Total request count across all the user's traffic in the last 7 days. */
  requests_7d?: number;
}

export interface ApiUserSearchResult {
  id: string;
  email: string;
  display_name: string;
  user_class: UserClass;
  user_class_reason: UserClassReason;
  user_class_source: UserClassSource | null;
  state: 'active' | 'tombstoned';
}

export interface ApiPlatformAdmin {
  user_id: string;
  email?: string;
  display_name?: string;
}

/**
 * A grantable platform capability held by a user (currently only
 * 'external-share'). email/display_name are joined from `users` server-side
 * (absent when the user row is missing).
 */
export interface ApiCapabilityRow {
  user_id: string;
  capability: string;
  granted_by?: string;
  granted_at: number;
  email?: string;
  display_name?: string;
}

export interface ApiAdminOverview {
  users: { total: number; active_recent_7d: number; never_logged_in: number };
  apps: { active: number; archived: number };
  namespaces: { total: number; ready: number; pending: number };
  groups: { total: number; directory: number; manual: number; virtual: number };
  llm_aliases: { total: number };
  platform_admins: { total: number };
  reconciliation: {
    latest_run_at: number | null;
    findings_total: number;
    findings_open: number;
  };
  /** Top-5 users by request volume across all apps in the last 7d. */
  top_users_7d: ReadonlyArray<{ value: string; user_id: string; total: number }>;
}

export interface ApiAdminOverviewResponse {
  computed_at: number;
  overview: ApiAdminOverview;
}

export interface ApiUserDetail {
  id: string;
  email: string;
  display_name: string;
  user_class: UserClass;
  user_class_reason: UserClassReason;
  user_class_source: UserClassSource | null;
  guest_enrollment_protection: GuestEnrollmentProtection;
  state: 'active' | 'tombstoned';
  first_login_at?: number;
  last_login_at?: number;
  user_namespace_id?: string;
  user_namespace_stub?: string | null;
}

export interface ApiUserGroupRef {
  id: string;
  external_id: string;
  display_name: string;
  /** True when this group is configured as a Guest classification source. */
  guest_group: boolean;
}

export interface ApiUserMembership {
  namespace_id: string;
  stub?: string;
  tier?: string;
  role: string;
}

/** Sensitive identity metadata: current member platform administrators only. */
export type ApiUserDebugRecord = Record<string, string | number | boolean | null>;
export interface ApiUserDebugAvailability {
  status: 'available' | 'unavailable' | 'not_retained' | 'not_supported' | 'not_configured';
  reason: string;
}
export interface ApiUserDebugHistory {
  records: ApiUserDebugRecord[];
  total: number;
  truncated: boolean;
  next_cursor: string | null;
}
export interface ApiUserDebugResponse {
  user: ApiUserDebugRecord & {
    id: string; email: string; display_name: string; kind: string; state: string;
    user_class: UserClass; first_login_at: number; last_login_at: number;
    user_class_reason: UserClassReason;
  };
  principal: ApiUserDebugRecord | null;
  platform_admin: ApiUserDebugRecord | null;
  groups: ApiUserDebugRecord[];
  namespaces: {
    ownership: ApiUserDebugRecord[];
    direct_roles: ApiUserDebugRecord[];
    group_roles: ApiUserDebugRecord[];
  };
  app_grants: ApiUserDebugRecord[];
  guest_classification: {
    state: ApiUserDebugRecord | null;
    current_evidence: ApiUserDebugRecord | null;
    positive_observations: ApiUserDebugRecord[];
    decisions: ApiUserDebugHistory;
    evidence_history: ApiUserDebugHistory;
  };
  oidc: {
    configuration: { issuer: string | null; client_id: string | null };
    verified_account: ApiUserDebugRecord | null;
    raw_claims: ApiUserDebugAvailability;
    group_claim:
      | { status: 'not_yet_consumed'; claim_name: 'vibe_group_ids' }
      | {
          status: 'consumed'; claim_name: 'vibe_group_ids'; receipt_id: string;
          issuer: string; client_id: string; issued_at: number; consumed_at: number;
          group_ids: string[];
        };
    corporate_trust: {
      status: 'available' | 'unavailable'; reason: string;
      issuer_matches_configuration: boolean | null; explicitly_denied: boolean;
    };
  };
  directory: {
    availability: ApiUserDebugAvailability;
    source_state: ApiUserDebugRecord | null;
    group_ingestion: ApiUserDebugRecord | null;
    account_binding: ApiUserDebugRecord | null;
    subject_anchors: ApiUserDebugRecord[];
    denial: ApiUserDebugRecord | null;
    reconciliation: ApiUserDebugRecord[];
    profile: OrgIdentityDiagnostic | null;
    identities: OrgIdentityDiagnostic['identities'];
    identity_availability: ApiUserDebugAvailability;
    okta_groups: ApiUserDebugAvailability;
    full_okta_profile: ApiUserDebugAvailability;
  };
  scim: ApiUserDebugAvailability;
  login_history: {
    window: { since: number; until: number; days: 7; page_size: 50 };
    timestamp_semantics: string;
    audit: ApiUserDebugHistory;
    sessions: ApiUserDebugHistory;
    availability: ApiUserDebugAvailability;
  };
  collected_at: number;
}

export interface ApiDeployFile {
  path: string;
  size: number;
  sha256: string;
  content_type: string;
}

export interface ApiDeployFileContent {
  path: string;
  content_type: string;
  content: string | null;
}

export type ApiReconciliationFindingKind =
  | 'r2_orphan'
  | 'r2_missing'
  | 'wfp_orphan'
  | 'wfp_missing'
  | 'principal_lonely'
  | 'd1_drift'
  | 'storage_drift';

export type ApiReconciliationResolution =
  | 'open'
  | 'cleaned'
  | 'ignored'
  | 'false_positive'
  | 'auto_resolved';

export interface ApiReconciliationRunSummary {
  run_id: string;
  run_at: number;
  findings_total: number;
  findings_by_kind: Record<string, number>;
  findings_open: number;
}

export interface ApiReconciliationFinding {
  id: string;
  run_id: string;
  run_at: number;
  finding_kind: ApiReconciliationFindingKind;
  finding_target: string;
  detail: Record<string, unknown>;
  resolution: ApiReconciliationResolution;
  resolved_at?: number;
  resolved_by?: string;
}

/**
 * One aggregated incident per logical (kind, target). The
 * /admin/reconciliation page renders this, not the raw per-run rows.
 */
export interface ApiReconciliationIncident {
  /** Representative row id (the latest open row, or latest closed if none open). */
  id: string;
  finding_kind: ApiReconciliationFindingKind;
  finding_target: string;
  detail: Record<string, unknown>;
  resolution: ApiReconciliationResolution;
  resolved_at?: number;
  resolved_by?: string;
  /** Earliest run_at across all rows for this (kind, target). */
  first_seen_at: number;
  /** Latest last_seen_run_at across all rows for this (kind, target). */
  last_seen_at: number;
  /** Number of rows (pre-dedupe history + dedupe-era touches). */
  times_seen: number;
}

export interface ApiReconciliationIncidentSummary {
  open: number;
  resolved: number;
  by_kind: Record<string, number>;
}

export interface ApiDnsSyncResult {
  checked: number;
  created: string[];
  already_present: number;
  errors: Array<{ stub: string; message: string }>;
}

export type ApiDnsState = 'present' | 'missing';
export type ApiTlsState = 'active' | 'pending' | 'inactive' | 'missing';

export interface ApiDnsStatus {
  namespace_id: string;
  namespace_stub: string;
  fqdn: string;
  dns: ApiDnsState;
  tls: ApiTlsState;
  cert_status_raw: string | null;
  hosts: string[];
  /**
   * Source of the data:
   *   - 'cache'   — both ready timestamps set; synthesized from D1
   *   - 'live'    — just hit Cloudflare (only on `?refresh=1` or the per-namespace endpoint)
   *   - 'pending' — namespace has never been fully ready; background cron will refresh
   */
  source?: 'cache' | 'live' | 'pending';
  /** Timestamp the namespace first became fully ready. Null if not yet ready. */
  ready_since?: number | null;
  /** App count in the namespace (active state). Present on the bulk admin response. */
  active_apps_count?: number;
  /** App count in the namespace (archived state). Present on the bulk admin response. */
  archived_apps_count?: number;
}

export interface ApiAdminDnsStatuses {
  statuses: ApiDnsStatus[];
}

export interface ApiReadinessProbe {
  deploy_id: string;
  hostname: string;
  started_at: number;
  last_probe_at: number | null;
  last_status: number | null;
  attempts: number;
  phase: 'propagating' | 'ready' | 'timeout' | 'superseded';
  ready_at: number | null;
}

export interface ApiReadinessResponse {
  probe: ApiReadinessProbe;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: string,
  ) {
    super(`ApiError ${status}: ${body}`);
  }
}

// Log + timeline wire shapes (matching mock and real CP).

export interface ApiRuntimeLog {
  ts: number;
  request_id: string;
  host: string;
  app_id?: string;
  /** Namespace id recorded on attributed runtime lines. */
  namespace_id?: string;
  /** Human-readable namespace stub, resolved by request-timeline responses. */
  namespace_stub?: string;
  path: string;
  method: string;
  status: number;
  duration_ms: number;
  cf_colo?: string;
  user_id?: string;
  /** Verified viewer account class at request time; legacy/capability rows omit it. */
  user_class?: UserClass;
  user_email?: string;
  ua?: string;
  ref?: string;
  kind?: 'static' | 'worker' | 'mcp' | 'llm';
  /** Number of app-stream messages (console.log/info/warn/error) joined to this request. Server-enriched on /logs. */
  message_count?: number;
  /** `<ErrorName>: <message>` excerpt when the dispatched app threw an uncaught exception. */
  error?: string;
  /** First app-stream error message correlated to this request, when present. */
  app_error?: string;
  /** JSON-RPC method when kind === 'mcp' (`tools/call`, `tools/list`, etc.). */
  mcp_method?: string;
  /** Tool name on `tools/call`; resource URI on `resources/read`. */
  mcp_tool?: string;
  /**
   * Whether the MCP call succeeded. `false` covers RPC errors AND tool
   * dispatches that returned `isError`. HTTP status alone is not enough —
   * every MCP RPC returns 200 regardless of the inner outcome.
   */
  mcp_ok?: boolean;
  /** `Mcp-Session-Id` header — threads multiple calls into a single logical session. */
  mcp_session_id?: string;
  /** OAuth client_id (DCR registration) that drove the call. */
  mcp_client_id?: string;
  /** Human-readable name from oauth_clients.client_name. */
  mcp_client_name?: string;
  /** JSON-RPC envelope `id` — present for non-notification calls. */
  mcp_rpc_id?: string | number;
  /**
   * Token-auth bookkeeping. Bearer and webhook requests populate token details;
   * signed blob downloads set `token_kind: 'blob_url'` while `user_id` carries
   * the minted-for principal. Cookie-authenticated requests omit these fields.
   */
  token_id?: string;
  token_kind?: 'app' | 'app_global' | 'webhook' | 'blob_url';
  token_name?: string;
  token_preview?: string;
  /** Full storage key for a signed-blob redemption. */
  blob_key?: string;
  /** Signed-blob token expiry as a Unix timestamp in milliseconds. */
  blob_expires_at_ms?: number;
  /** Storage scope encoded in a signed-blob redemption token. */
  blob_scope?: 'app' | 'user';
  /** User principal whose isolated storage contains the blob. */
  blob_storage_principal?: string;
  /** Resolved email for the storage principal, when it maps to a user. */
  blob_storage_principal_email?: string;
  /** Stable app data handle that owns the blob bytes. */
  blob_data_handle_id?: string;
  /** Client source IP (`cf-connecting-ip`) when CF supplied it. */
  source_ip?: string;
}

/** Admin cross-app log row — runtime log enriched with the owning app's name, namespace, and owner. */
export interface ApiAdminLog extends ApiRuntimeLog {
  /** Owning app's display name. */
  app_name: string;
  /** Namespace stub for the owning app (e.g. "engineering", "kyle-osborn"). */
  namespace_stub: string;
  /** Okta sub of the app's primary owner. */
  owner_user_id: string;
  /** Email of the app's primary owner. Absent for tombstoned users. */
  owner_email?: string;
}

export interface ApiAdminLogsResponse {
  logs: ApiAdminLog[];
  /** Number of apps that were scanned to produce this page. */
  apps_scanned: number;
  /** Opaque cursor — when present, more results exist beyond this page. */
  next_cursor?: string;
}

export interface ApiLogsHistogramBucket {
  ts: number;
  total: number;
  errors: number;
  /** Subset of `total` that came from MCP function calls (kind === 'mcp'). */
  mcp_total?: number;
}

export interface ApiLogsHistogramResponse {
  buckets: ApiLogsHistogramBucket[];
  /** Bucket size in milliseconds. */
  bucket_ms: number;
}

export interface ApiDeploysHistogramBucket {
  ts: number;
  total: number;
  worker: number;
  static: number;
  /** Deploys whose current state is `rolled_back` (i.e. were rolled back away from). */
  rolled_back: number;
}

export interface ApiDeploysHistogramResponse {
  buckets: ApiDeploysHistogramBucket[];
  /** Bucket size in milliseconds. */
  bucket_ms: number;
}

export interface ApiDailyUniqueUsersBucket {
  bucket_ts: number;
  unique_users: number;
}

export interface ApiDailyUniqueUsersResponse {
  buckets: ApiDailyUniqueUsersBucket[];
  bucket_ms: number;
  /** Legacy: number of app DOs scanned by the old fan-out. Omitted now that the
   *  series is served from the pre-aggregated metric_rollups table. */
  apps_scanned?: number;
}

// ─── MetricsDO query API (slice 2/3) ─────────────────────────────────────

export type ApiMetricsStatusClass = '1xx' | '2xx' | '3xx' | '4xx' | '5xx';
export type ApiMetricsMethod =
  | 'GET'
  | 'POST'
  | 'PUT'
  | 'PATCH'
  | 'DELETE'
  | 'HEAD'
  | 'OPTIONS'
  | 'OTHER';
export type ApiMetricsResolution = 'minute' | 'hour' | 'day';
export type ApiMetricsGroupBy = 'status_class' | 'method' | 'path_prefix' | 'kind';

/**
 * One row per (bucket_ts, group_by-dim-values). When no group_by is requested
 * the dim fields are absent and there's at most one row per bucket_ts.
 */
export interface ApiMetricsBucket {
  bucket_ts: number;
  total: number;
  duration_sum: number;
  status_class?: ApiMetricsStatusClass;
  method?: ApiMetricsMethod;
  path_prefix?: string;
  kind?: 'static' | 'worker' | 'mcp' | 'llm';
}

export interface ApiMetricsHistogramResponse {
  buckets: ApiMetricsBucket[];
  resolution: ApiMetricsResolution;
  total: number;
  /** Only present on /admin/metrics/histogram — number of app DOs queried. */
  apps_scanned?: number;
}

export interface ApiMetricsTopRow {
  value: string;
  total: number;
  duration_sum: number;
}

export interface ApiMetricsTopResponse {
  dim: ApiMetricsGroupBy;
  ranked: ApiMetricsTopRow[];
  apps_scanned: number;
}

export interface ApiMetricsTopAppRow extends ApiMetricsTopRow {
  app_id: string;
}

export interface ApiMetricsTopNamespaceRow extends ApiMetricsTopRow {
  namespace_id: string;
}

export interface ApiMetricsTopUserRow extends ApiMetricsTopRow {
  /** Okta sub when email unknown; `value` is the email or sub fallback. */
  user_id: string;
}

export interface ApiMetricsTopByAppResponse {
  apps: ApiMetricsTopAppRow[];
  namespaces: ApiMetricsTopNamespaceRow[];
  users: ApiMetricsTopUserRow[];
  apps_scanned: number;
}

export interface ApiAppMetricsTopUsersResponse {
  ranked: ApiMetricsTopUserRow[];
  app_id: string;
}

export interface ApiAppMetricsSummary {
  app_id: string;
  from_ms: number;
  to_ms: number;
  total: number;
  errors: number;
  /** Sum of request durations in ms across the window. Avg = duration_sum / total. */
  duration_sum: number;
  unique_users: number;
}

export interface ApiNamespaceMetricsTopUsersResponse {
  ranked: ApiMetricsTopUserRow[];
  namespace_id: string;
  apps_scanned: number;
}

export interface ApiUserMetricsTopAppRow extends ApiMetricsTopRow {
  app_id: string;
  namespace_id: string;
  namespace_stub: string;
}

export interface ApiUserMetricsTopAppsResponse {
  ranked: ApiUserMetricsTopAppRow[];
  user_id: string;
  apps_scanned: number;
}

export interface ApiMyMetricsResponse {
  histogram: {
    buckets: Array<{
      bucket_ts: number;
      total: number;
      errors: number;
      /** Subset of `total` from MCP function calls (kind === 'mcp'). */
      mcp_total?: number;
    }>;
    bucket_ms: number;
  };
  top_apps: ApiUserMetricsTopAppRow[];
  top_users: ApiMetricsTopUserRow[];
  top_paths: ApiMetricsTopRow[];
  apps_scanned: number;
}

/**
 * Admin/self view of /api/v1/users/<id>/metrics — same payload as
 * ApiMyMetricsResponse plus the user's own deploys (bucketed daily).
 */
export interface ApiUserOwnedAppMetricsResponse extends ApiMyMetricsResponse {
  deploys_histogram: ApiDeploysHistogramResponse;
}

export interface ApiAppLog {
  ts: number;
  request_id: string;
  app_id: string;
  level: 'log' | 'info' | 'warn' | 'error';
  message: string;
  source: 'app';
}

export interface ApiAuditTimelineEntry {
  stream: 'audit';
  id: string;
  action_id: string;
  actor_user_id: string;
  /** Display name from users table; absent if the user row is gone. */
  actor_display_name?: string;
  /** Email from users table; absent if the user row is gone. */
  actor_email?: string;
  action: string;
  target_kind: string;
  target_id: string;
  created_at: number;
  before_json?: string;
  after_json?: string;
  request_id?: string;
  /** Raw User-Agent header for the mutation. 'mcp' marks cp-internal RPC calls. */
  user_agent?: string;
  /** api_tokens.id when the mutation authenticated via PAT; absent for cookie auth. */
  auth_token_id?: string;
  /** api_tokens.name — present when auth_token_id resolved against api_tokens. */
  auth_token_name?: string;
  /** api_tokens.kind — 'control_plane' | 'app' | 'app_global' | 'webhook' | 'deploy'. */
  auth_token_kind?: string;
  /** Client source IP recorded at mutation time; absent on older rows. */
  ip?: string;
  /** Mcp-Session-Id when the mutation came through the platform MCP server; absent otherwise. */
  mcp_session_id?: string;
}

// The mock returns a simplified event entry shape (stream: 'ingress' | 'worker').
export interface ApiTimelineEvent {
  ts: number;
  stream: string;
  event: string;
  request_id: string;
}

export type ApiTimelineEntry =
  | (ApiRuntimeLog & { stream: 'runtime' })
  | (ApiAppLog & { stream: 'app' })
  | ApiAuditTimelineEntry
  | ApiTimelineEvent;

export interface ApiTimelineResponse {
  timeline: ApiTimelineEntry[];
}

// ─── MCP OAuth clients + grants ───────────────────────────────────────

export interface ApiMcpClient {
  client_id: string;
  client_name: string;
  redirect_uris: string[];
  scopes: string[];
  created_at: number;
  disabled_at: number | null;
}

export interface ApiMcpGrant {
  client_id: string;
  client_name: string;
  granted_at: number;
}

// ─── Personal access tokens ────────────────────────────────────────────

export type ApiTokenKind = 'control_plane' | 'app' | 'app_global' | 'webhook' | 'deploy';

/**
 * Machine-identity provenance attached to a control-plane PAT at mint time.
 * All fields are optional — the CLI sends whatever it can determine about the
 * machine and OS user; unrecognised fields are stored as-is and surfaced here.
 */
export interface ApiTokenProvenance {
  hostname?: string;
  machine_serial?: string;
  os_user?: string;
  platform?: string;
  cli_version?: string;
}

export interface ApiPersonalAccessToken {
  id: string;
  name: string;
  /** Non-secret display fragment: `mg_pat_xxxx…yyyy`, `mg_fdat_xxxx…yyyy`, `mg_wt_xxxx…yyyy`, or `mg_deploy_xxxx…yyyy`. */
  token_preview: string;
  kind: ApiTokenKind;
  /** Set when kind='app'/'webhook' or app-scoped 'deploy'; null for kind='control_plane' or namespace-scoped 'deploy'. */
  app_id: string | null;
  /** Set on deploy keys scoped to a namespace (kind='deploy'). */
  namespace_id?: string | null;
  /** Free-text provenance label; set only for kind='webhook'. */
  webhook_label?: string | null;
  /** Machine-identity provenance captured at mint time; present only for kind='control_plane' PATs minted by the CLI. */
  provenance?: ApiTokenProvenance | null;
  created_at: number;
  expires_at: number;
  last_used_at: number | null;
  revoked_at: number | null;
}

/** One recent request made with a webhook token (from the activity endpoint). */
export interface ApiWebhookActivityEntry {
  ts: number;
  request_id: string;
  method: string;
  path: string;
  status: number;
  source_ip?: string;
}

export interface ApiWebhookTokenActivity {
  last_used_at: number | null;
  activity: ApiWebhookActivityEntry[];
}

// ─── Storage observability ─────────────────────────────────────────────

export interface ApiStorageUsage {
  app_id?: string;
  do_id?: string;
  do_name?: string;
  resource_version?: 'v1' | 'v2';
  resource_scope?: 'app' | 'user';
  bytes?: number;
  bytes_limit?: number;
  tables?: ReadonlyArray<{ name: string; rows: number }>;
  /** Set when the API can't return numbers — e.g. per-user-scoped apps without a `user_id`. */
  scope?: 'user';
  message?: string;
}

export interface ApiStorageQueryResult {
  ok: boolean;
  do_id?: string;
  do_name: string;
  duration_ms: number;
  results?: ReadonlyArray<Record<string, unknown>>;
  meta?: { rowsRead: number; rowsWritten: number };
  error?: string;
  scope?: 'app' | 'user';
}

export interface ApiStorageTable {
  name: string;
  rows: number;
}

export interface ApiStorageTablesResult {
  scope: 'app' | 'user';
  do_name: string;
  tables: ApiStorageTable[];
}

export interface ApiBlobEntry {
  key: string;
  size: number;
  content_type: string;
  uploaded_at_ms: number;
}

export interface ApiBlobList {
  scope: 'app' | 'user';
  keys: ApiBlobEntry[];
  cursor: string | null;
  truncated: boolean;
}

export interface ApiKvList {
  scope: 'app' | 'user';
  supported: boolean;
  message?: string;
  keys: string[];
}

export interface ApiKvValue {
  key: string;
  value: string;
}

// ─── LLM usage dashboards ──────────────────────────────────────────────
//
// Four endpoints under /api/v1/*/llm-usage, each returning a different
// shape based on who's allowed to see what:
//   - ApiAppLlmUsage     : owner/admin view of one app
//   - ApiAppLlmUsageMe   : viewer's own slice against one app
//   - ApiMyLlmUsage      : viewer's totals + per-app breakdown
//   - ApiAdminLlmUsage   : platform-wide (admin only)

export interface LlmUsageTotals {
  calls: number;
  errors: number;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  cost_usd: number;
}

export interface LlmUsageDailyPoint {
  /** YYYY-MM-DD UTC. */
  day: string;
  calls: number;
  total_tokens: number;
  cost_usd: number;
}

export interface LlmUsageByUserRow {
  user_id: string;
  user: { name: string; email: string } | null;
  calls: number;
  total_tokens: number;
  cost_usd: number;
}

export interface LlmUsageByAppRow {
  app_id: string;
  app: { name: string; hostname: string; namespace_id: string } | null;
  calls: number;
  total_tokens: number;
  cost_usd: number;
}

export interface LlmUsageByModelRow {
  model: string;
  alias: string | null;
  calls: number;
  total_tokens: number;
  cost_usd: number;
}

export interface ApiAppLlmUsage {
  range_days: number;
  totals: LlmUsageTotals;
  by_user: LlmUsageByUserRow[];
  by_model: LlmUsageByModelRow[];
  daily: LlmUsageDailyPoint[];
}

export interface ApiAppLlmUsageMe {
  range_days: number;
  totals: LlmUsageTotals;
}

export interface ApiMyLlmUsage {
  range_days: number;
  totals: LlmUsageTotals;
  by_app: LlmUsageByAppRow[];
  daily: LlmUsageDailyPoint[];
}

export interface ApiAdminLlmUsage {
  range_days: number;
  totals: LlmUsageTotals;
  top_apps: Array<LlmUsageByAppRow & { distinct_users: number }>;
  top_users: Array<LlmUsageByUserRow & { distinct_apps: number }>;
  by_model: LlmUsageByModelRow[];
  daily: LlmUsageDailyPoint[];
}

// ─── App migration (Plan 3) ──────────────────────────────────────────────────
// Wire shape returned by GET /api/v1/apps/<id>/inventory. The same envelope
// optionally carries `destination_check` when the caller passes
// ?destination_namespace_id=<id> — the console's Migrate panel reuses this
// for the name-conflict preflight rather than calling a separate endpoint.
export interface ApiAppInventoryTable {
  name: string;
  rowCount: number;
  byteSize: number;
}
export interface ApiAppInventoryPrincipal {
  principalId: string;
  kind: string;
  rowCount: number;
  byteSize: number;
}
export interface ApiAppInventory {
  app_id: string;
  namespace_id: string;
  data_handle_id: string | null;
  keying: 'handle' | 'legacy';
  deploys: { count: number };
  grants: { count: number };
  app_scope_storage: {
    version: 'v1' | 'v2' | null;
    exists: boolean;
    row_count: number;
    byte_size: number;
    tables: ReadonlyArray<ApiAppInventoryTable>;
  };
  user_scope_storage: {
    scope: 'app' | 'user';
    principal_count: number;
    total_row_count: number;
    total_byte_size: number;
    per_principal: ReadonlyArray<ApiAppInventoryPrincipal>;
  };
  blob_storage: {
    app_scope: { object_count: number; byte_size: number };
    user_scope: { object_count: number; byte_size: number };
  };
  summary: {
    total_objects: number;
    total_bytes: number;
    estimated_migration_seconds: number;
  };
  destination_check?: {
    name_conflict: boolean;
    suggested_names?: ReadonlyArray<string>;
  };
}

export interface ApiMigrateSuccess {
  ok: true;
  old_hostname: string;
  new_hostname: string;
  old_name: string;
  new_name: string;
  sharing_before: Sharing;
  sharing_after: Sharing;
  sharing_downgraded: boolean;
}

/**
 * Error shape returned by POST /api/v1/apps/<id>/migrate on non-2xx. The
 * client throws `ApiError` for these; consumers parse the body for the
 * specific error code + downgrade/conflict details.
 */
export interface ApiMigrateError {
  error:
    | 'name_conflict'
    | 'sharing_downgrade_required'
    | 'policy_violation'
    | 'invalid_name'
    | 'migration_in_progress'
    | 'missing_destination'
    | 'not_found'
    | 'migrate_failed';
  message: string;
  suggested_names?: ReadonlyArray<string>;
  proposed_sharing?: Sharing;
}
